import React, { useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { AnalyticsOverview } from "../../../../app/data/invoice-factoring/models";
import styles from "../../../../sass/variables.module.scss";
import { DOCUMENT_GAP_LABELS, formatCents } from "../constants";
import { formatCompactCents, formatPayor, formatPeriod } from "../utils/metrics";
import { TrendMetric } from "./metricToggle";

ChartJS.register(BarElement, CategoryScale, Legend, LinearScale, LineElement, PointElement, Tooltip);

const PAYOR_COLORS = [styles.blue1, styles.green1, styles.orange, styles.purple, styles.yellow];

const metricAxis = (metric: TrendMetric) =>
  metric === "value"
    ? { beginAtZero: true, ticks: { callback: formatCompactCents }, title: { display: true, text: "Invoice value" } }
    : { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: "Invoices" } };

const formatMetric = (metric: TrendMetric, value: unknown) =>
  metric === "value" ? formatCents(Number(value)) : `${value}`;

export const SubmissionTrendChart: React.FC<{ overview: AnalyticsOverview; metric: TrendMetric }> = ({
  overview,
  metric,
}) => {
  const { trend, range } = overview;

  const data = useMemo(
    () => ({
      labels: trend.map((point) => formatPeriod(point.period, range.granularity)),
      datasets: [
        {
          label: "Invoices tracked",
          data: trend.map((point) => (metric === "value" ? point.trackedValueCents : point.tracked)),
          borderColor: styles.blue1,
          backgroundColor: styles.blue1,
          tension: 0.2,
        },
        {
          label: "Submissions completed",
          data: trend.map((point) => (metric === "value" ? point.submittedValueCents : point.submitted)),
          borderColor: styles.green1,
          backgroundColor: styles.green1,
          tension: 0.2,
        },
      ],
    }),
    [trend, range.granularity, metric]
  );

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom" },
        tooltip: { callbacks: { label: (item) => `${item.dataset.label}: ${formatMetric(metric, item.raw)}` } },
      },
      scales: { y: metricAxis(metric) },
    }),
    [metric]
  );

  return (
    <div className="xgs-invoice-factoring__chart">
      <Line data={data} options={options} />
    </div>
  );
};

export const PayorTrendChart: React.FC<{ overview: AnalyticsOverview; metric: TrendMetric }> = ({ overview, metric }) => {
  const { trend, range, payors } = overview;

  const data = useMemo(
    () => ({
      labels: trend.map((point) => formatPeriod(point.period, range.granularity)),
      datasets: payors.series.map((series, index) => {
        const color = series.key === "OTHER" ? styles.gray4 : PAYOR_COLORS[index % PAYOR_COLORS.length];
        return {
          label: series.key === "OTHER" ? "All other payors" : formatPayor(series.payorName, series.payorNumber),
          data: metric === "value" ? series.valueCents : series.tracked,
          backgroundColor: color,
          borderColor: color,
          maxBarThickness: 48,
        };
      }),
    }),
    [trend, range.granularity, payors.series, metric]
  );

  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom" },
        tooltip: { callbacks: { label: (item) => `${item.dataset.label}: ${formatMetric(metric, item.raw)}` } },
      },
      scales: { x: { stacked: true }, y: { ...metricAxis(metric), stacked: true } },
    }),
    [metric]
  );

  return (
    <div className="xgs-invoice-factoring__chart">
      <Bar data={data} options={options} />
    </div>
  );
};

export const DocumentGapChart: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => {
  const { buckets } = overview.documentGaps;

  const data = useMemo(
    () => ({
      labels: buckets.map((bucket) => DOCUMENT_GAP_LABELS[bucket.bucket]),
      datasets: [
        {
          label: "Invoices with a job but documents not sent",
          data: buckets.map((bucket) => bucket.count),
          backgroundColor: [styles.green1, styles.blue1, styles.orange, styles.red],
          maxBarThickness: 56,
        },
      ],
    }),
    [buckets]
  );

  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => `${item.raw} invoices, ${formatCents(buckets[item.dataIndex]?.valueCents)}`,
          },
        },
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: "Invoices" } },
        x: { title: { display: true, text: "Time since the Denim job was created" } },
      },
    }),
    [buckets]
  );

  return (
    <div className="xgs-invoice-factoring__chart">
      <Bar data={data} options={options} />
    </div>
  );
};
